/**
 * FAQ / .accordion_item-basic accordions (gosse-faq, product FAQs, mobile menu items, etc.)
 * Uses [data-accordion-container] + event delegation so theme editor reloads work (new DOM = new bind).
 */

class AccordionManager {
  constructor() {
    this.TRANSITION_DURATION = 250;
    /** @type {WeakMap<Element, boolean>} */
    this.animating = new WeakMap();
    /** @type {WeakSet<Element>} */
    this.boundContainers = new WeakSet();
  }

  escapeId(id) {
    if (typeof id !== "string") return "";
    return typeof CSS !== "undefined" && CSS.escape ? CSS.escape(id) : id.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  }

  /**
   * @param {string} sectionId - must match data-accordion-container and data-accordion-section on items
   */
  initSection(sectionId) {
    const esc = this.escapeId(sectionId);
    const container = document.querySelector(
      `[data-accordion-container="${esc}"]`
    );
    if (!container || this.boundContainers.has(container)) return;
    this.boundContainers.add(container);

    const collapses = container.querySelectorAll(".accordion-collapse");
    collapses.forEach((collapse) => {
      collapse.style.height = "0";
    });

    container.addEventListener("click", (evt) => {
      const button = evt.target.closest("[data-accordion-toggle]");
      if (!button || !container.contains(button)) return;
      evt.preventDefault();
      this.handleAccordion(container, sectionId, button);
    });

    if (container.hasAttribute("data-accordion-open-first")) {
      const firstBtn = container.querySelector(
        `[data-accordion-section="${this.escapeId(sectionId)}"] [data-accordion-toggle]`
      );
      if (firstBtn && firstBtn.getAttribute("aria-expanded") !== "true") {
        queueMicrotask(() => {
          this.handleAccordion(container, sectionId, firstBtn);
        });
      }
    }
  }

  setAnimating(container, value) {
    this.animating.set(container, value);
  }

  isBusy(container) {
    return this.animating.get(container) === true;
  }

  itemRoot(btn) {
    return btn.closest(".accordion_item-basic") || btn.closest(".faq-basic");
  }

  handleAccordion(container, sectionId, button) {
    if (this.isBusy(container)) return;

    const isExpanded = button.getAttribute("aria-expanded") === "true";
    const targetId = button.getAttribute("aria-controls");
    const targetCollapse = targetId ? document.getElementById(targetId) : null;

    if (!targetCollapse || targetCollapse.classList.contains("collapsing")) return;

    this.setAnimating(container, true);

    const selectorButtons = `[data-accordion-section="${this.escapeId(sectionId)}"] [data-accordion-toggle]`;
    const selectorCollapses = `[data-accordion-section="${this.escapeId(sectionId)}"] .accordion-collapse`;

    const allButtons = container.querySelectorAll(selectorButtons);
    const allCollapses = container.querySelectorAll(selectorCollapses);

    // Close all other accordions in this container
    allButtons.forEach((btn) => {
      if (btn === button) return;
      const otherTargetId = btn.getAttribute("aria-controls");
      const otherCollapse = otherTargetId
        ? document.getElementById(otherTargetId)
        : null;
      if (
        !otherCollapse ||
        !otherCollapse.classList.contains("show")
      )
        return;

      btn.setAttribute("aria-expanded", "false");
      otherCollapse.classList.add("collapsing");
      otherCollapse.classList.remove("show");

      otherCollapse.style.height = `${otherCollapse.scrollHeight}px`;
      void otherCollapse.offsetHeight;
      otherCollapse.style.height = "0";

      setTimeout(() => {
        otherCollapse.classList.remove("collapsing");
        const item = this.itemRoot(btn);
        if (item) item.classList.remove("active");
      }, this.TRANSITION_DURATION);
    });

    const item = this.itemRoot(button);

    // Toggle current accordion
    if (!isExpanded) {
      button.setAttribute("aria-expanded", "true");
      targetCollapse.classList.add("collapsing", "show");
      if (item) item.classList.add("active");

      targetCollapse.style.height = "0";
      const contentHeight = targetCollapse.scrollHeight;
      targetCollapse.style.height = `${contentHeight}px`;

      setTimeout(() => {
        targetCollapse.classList.remove("collapsing");
        targetCollapse.style.height = "auto";
        this.setAnimating(container, false);
      }, this.TRANSITION_DURATION);
    } else {
      button.setAttribute("aria-expanded", "false");
      targetCollapse.classList.add("collapsing");
      targetCollapse.classList.remove("show");

      targetCollapse.style.height = `${targetCollapse.scrollHeight}px`;
      void targetCollapse.offsetHeight;
      targetCollapse.style.height = "0";

      setTimeout(() => {
        targetCollapse.classList.remove("collapsing");
        if (item) item.classList.remove("active");
        this.setAnimating(container, false);
      }, this.TRANSITION_DURATION);
    }
  }
}

const accordionManager = new AccordionManager();

window.initAccordionSection = (sectionId) => {
  accordionManager.initSection(sectionId);
};

document.addEventListener("shopify:section:load", (ev) => {
  const root = ev.target;
  if (!root || !root.querySelector) return;
  const container = root.querySelector("[data-accordion-container]");
  if (!container) return;
  const sid = container.getAttribute("data-accordion-container");
  if (sid) accordionManager.initSection(sid);
});
